import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Sitter, SitterService } from 'us-web-services';
import { fullName } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

type InterviewType = 'phone' | 'in-person' | 'paid';

interface InterviewOption {
  type: InterviewType;
  bgClass: string;
  kickerKey: string;
  titleKey: string;
  bodyKey: string;
}

// Mirrors apps/angular/.../interview-selection.tpl.html — kicker text, a
// clickable bg-image tile, then title and body. Each option uses the SCSS
// classes ported in apps/react/src/index.scss.
const OPTIONS: InterviewOption[] = [
  {
    type: 'phone',
    bgClass: 'bkgd-img-couple-steps',
    kickerKey: 'pages.interviewSelection.phone.kicker',
    titleKey: 'pages.interviewSelection.phone.title',
    bodyKey: 'pages.interviewSelection.phone.body',
  },
  {
    type: 'in-person',
    bgClass: 'bkgd-img-yoga-sitter-kitchen',
    kickerKey: 'pages.interviewSelection.inPerson.kicker',
    titleKey: 'pages.interviewSelection.inPerson.title',
    bodyKey: 'pages.interviewSelection.inPerson.body',
  },
  {
    type: 'paid',
    bgClass: 'bkgd-img-sitter-baby-sitting',
    kickerKey: 'pages.interviewSelection.paid.kicker',
    titleKey: 'pages.interviewSelection.paid.title',
    bodyKey: 'pages.interviewSelection.paid.body',
  },
];

export const InterviewSelection = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const sitterIdParam = searchParams.get('sitterId');
  const sitterId = sitterIdParam ? Number(sitterIdParam) : undefined;

  const [sitter, setSitter] = useState<Sitter | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Interview Options';
  }, []);

  useEffect(() => {
    if (!sitterId || !user?.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId: user.id, parentId: user.id } });
        if (cancelled) return;
        setSitter(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[InterviewSelection] sitter load failed', err);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [sitterId, user?.id]);

  const selectType = (type: InterviewType): void => {
    tracker.track('booking-details', { action: `Interview flow started: ${type}` });
    const next = new URLSearchParams(searchParams);
    next.set('interviewType', type);
    next.set('typeId', '1');
    navigate(`/parent/bookings/select-date?${next.toString()}`);
  };

  return (
    <div className='container py-8'>
      {sitter && (
        <div className='flex items-center gap-4 mb-8 max-w-4xl'>
          {sitter.pictureUrl && (
            <img
              src={sitter.pictureUrl}
              alt={fullName(sitter)}
              className='w-24 h-32 rounded object-cover'
            />
          )}
          <h1 className='text-3xl font-semibold'>
            {t('pages.interviewSelection.heading', { name: sitter.firstName ?? '' })}
          </h1>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl md:px-12'>
        {OPTIONS.map((opt) => (
          <div key={opt.type}>
            <h3 className='text-sapphire font-semibold mb-1'>{t(opt.kickerKey)}</h3>
            <Button
              id={`select-${opt.type}-interview`}
              buttonType={ButtonType.tertiary}
              onClick={(): void => selectType(opt.type)}
              className={`${opt.bgClass} block w-full h-48 mb-3 rounded p-0 border-0`}
              aria-label={t(opt.titleKey)}
            />
            <h3 className='font-bold mb-2'>{t(opt.titleKey)}</h3>
            <p className='text-neutral-700'>{t(opt.bodyKey)}</p>
          </div>
        ))}
      </div>
    </div>
  );
});
